<template>
	<app-page>
		<app-heading :title="`${unit.name} (${unit.code})`" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="primary">Save</app-button>
					<app-button
						type="cancel"
						small
						:to="{ name: 'website-units', params: { website: websiteId } }"
					>
						Cancel
					</app-button>
				</app-section-actions>
				<div class="title">Language</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-select
							v-model="locale"
							:items="locales"
							label="Language"
						/>
					</app-cell>
				</app-row>
				<div class="title">URL &amp; Content</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text v-if="locale === 'en'"
							label="URI"
							v-model="uriName"
							required
							:errors="errors.uri_name"
						/>
						<input-text v-else
							label="Translated URI"
							:placeholder="uriName"
							v-model="uriTranslations[locale]"
							hint="Write a new URI only if you intent to translate the original URI"
							:errors="errors.uri_translations"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" sm="6">
						<input-text
							label="Display name"
							v-model="displayName[locale]"
							:placeholder="unit.translations
								? (unit.translations[locale] || unit.translations.en)
								: unit.name"
							:errors="errors.display_name"
						/>
					</app-cell>
				</app-row>
				<div class="title">
					<span>SEO</span>
					<app-button
						color="secondary"
						small
						flat
						class="ml-2"
						@click="onDetectConflicts"
					>Detect conflicts</app-button>
				</div>
				<div v-if="locale">
					<app-row
						v-for="(conflict, i) in getConflicts('title')"
						:key="`conflict-title-${i}`"
					>
						<app-cell cols="12" sm="6">
							<fragment-seo-conflict :conflict="conflict" />
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Meta title"
								v-model="meta.title[locale]"
								:placeholder="unit.translations
									? (unit.translations[locale] || unit.translations.en)
									: unit.name"
								required
								single-line
								counter="60"
								:errors="errors.title"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('description')"
						:key="`conflict-description-${i}`"
					>
						<app-cell cols="12" sm="6">
							<div class="pa-2 warning lighten-3">
								<fragment-seo-conflict :conflict="conflict" />
							</div>
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Meta description"
								v-model="meta.description[locale]"
								:placeholder="unit.short_description
									? unit.short_description[locale] || unit.short_description.en
									: null"
								required
								counter="160"
								:errors="errors.description"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('heading')"
						:key="`conflict-heading-${i}`"
					>
						<app-cell cols="12" sm="6">
							<div class="pa-2 warning lighten-3">
								<fragment-seo-conflict :conflict="conflict" />
							</div>
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Heading (H1)"
								v-model="meta.heading[locale]"
								:placeholder="unit.translations
									? (unit.translations[locale] || unit.translations.en)
									: unit.name"
								required
								single-line
								counter="70"
								:errors="errors.heading"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('heading2')"
						:key="`conflict-heading2-${i}`"
					>
						<app-cell cols="12" sm="6">
							<div class="pa-2 warning lighten-3">
								<fragment-seo-conflict :conflict="conflict" />
							</div>
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Sub-heading (H2)"
								v-model="meta.heading2[locale]"
								required
								counter="200"
								rows="2"
								:errors="errors.heading2"
							/>
						</app-cell>
					</app-row>
				</div>
			</app-section>
		</app-form>
	</app-page>
</template>

<script>
import { mapValues } from 'lodash'

import FragmentSeoConflict from '@/components/fragments/FragmentSeoConflict.vue'
import locales from '@/datasources/locales'
import { clearEmptyFields } from '@/utils/collection'

export default {
	components: { FragmentSeoConflict },
	data() {
		return {
			locale: null,
			website: {
				locales: [],
			},
			unit: {
				code: '',
				name: '',
				translations: {},
				description: {},
			},
			errors: {},
			meta: {
				title: {},
				description: {},
				heading: {},
				heading2: {},
			},
			conflicts: [],
			uriName: '',
			displayName: {},
			uriTranslations: {},
		}
	},
	async mounted() {
		this.$store.dispatch('app/startLoading')
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['locales'])
			.include(this.$ravello.graphql('unit', this.id)
				.fields(['uri_name, uri_translations, display_name'])
				.include(this.$ravello.graphql('meta').fields([
					'title',
					'description',
					'heading',
					'heading2',
				]))
				.include(this.$ravello.graphql('unit').fields([
					'name',
					'code',
					'translations',
					'short_description',
				])))
			.query()
		this.unit = this.website.unit.unit

		this.meta = mapValues(this.website.unit.meta, (value) => {
			const metaValue = value ? { ...value } : {}

			this.website.locales.forEach((locale) => {
				if (!metaValue[locale]) {
					metaValue[locale] = ''
				}
			})
			return metaValue
		})
		this.uriName = this.website.unit.uri_name
		this.displayName = this.website.unit.display_name ? { ...this.website.unit.display_name } : {}
		this.uriTranslations = this.website.unit.uri_translations
			? { ...this.website.unit.uri_translations } : {}
		this.website.locales.forEach((locale) => {
			if (!this.displayName[locale]) {
				this.displayName[locale] = ''
			}
			if (locale !== 'en' && !this.uriTranslations[locale]) {
				this.uriTranslations[locale] = ''
			}
		})

		const [locale] = this.website.locales
		this.locale = locale
		this.$store.dispatch('app/stopLoading')
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		id() {
			return this.$route.params.unit
		},
		locales() {
			return this.website.locales.map(locale => ({
				text: locales[locale].name,
				value: locale,
			}))
		},
	},
	methods: {
		async injectConflictedUnits(conflicts) {
			const ids = Object.keys(conflicts.reduce((acc, conflict) => ({
				...acc,
				[conflict.unit]: true,
			}), {}))

			if (!ids.length) { return [] }
			const units = await this.$ravello.graphql('units')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.include(this.$ravello.graphql('unit').fields(['_id', 'code', 'name']))
				.filter('unit', ids)
				.limit(ids.length)
				.query()

			const unitsMap = units.reduce((acc, unit) => ({
				...acc,
				[unit.unit._id]: unit.unit,
			}), {})

			return conflicts.map(conflict => ({
				...conflict,
				res: unitsMap[conflict.unit] || {},
			}))
		},
		async onDetectConflicts() {
			this.$store.dispatch('app/startLoading')
			try {
				const conflicts = await this.$ravello.request().v(2)
					.to('websites')
					.to(this.websiteId)
					.to('units')
					.to(this.id)
					.to('detectconflicts')
					.create({
						...this.meta,
					})

				this.conflicts = await this.injectConflictedUnits(conflicts)
				this.$store.dispatch('app/stopLoading')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
				this.$store.dispatch('app/stopLoading')
			}
		},

		getConflicts(field) {
			return (this.conflicts || []).filter(conflict => conflict.field === field)
		},
		async onSubmit() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request().v(2)
					.to('websites')
					.to(this.websiteId)
					.to('units')
					.to(this.id)
					.update({
						meta: this.meta,
						uri_name: this.uriName,
						uri_translations: clearEmptyFields(this.uriTranslations),
						display_name: this.displayName,
					})
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `${this.unit.code} successfully updated`)
				this.$router.push({ name: 'website-units', params: { website: this.websiteId } })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
	},
}
</script>
